import { useMemo } from 'react';
import { todayISO } from '../domain/clock';
import { bookedLessonsOf, forecastHorizon } from '../domain/packageSale';

// A package running out, for one student (#181).
//
// The fold is `packages::forecast` in the Rust core, which argues every number
// it produces. The rules — the horizon, which lessons are booked and how each
// is shaped, and what `Apply Package Time → Existing events…` converts — are
// src/domain/packageSale.js's, shared with the phones and re-exported here.
//
// ── Which lessons are handed to the fold (#347) ────────────────────────────
//
//   * Unrecorded only. Marking attendance writes a `consume`
//     (`useAttendance.markAttendance`), so a marked lesson is already in the
//     entries the fold walks, and counting it again would show the student
//     short by time already given.
//   * Past ones included, and counted apart. An unmarked lesson last week will
//     draw on the package when somebody marks it; leaving it out would make
//     `Available` too high. The fold puts it in `unmarkedBehind` rather than in
//     `upcoming`, and `today` tells it which is which.
//
// ── Where the lessons come from, and how far they reach ───────────────────
//
// `history` is `useStudentAttendance`'s fold — this student's occurrences over
// its window with a status attached — rather than a second
// `occurrencesBetween` call: the window is a cache key in `useEvents`, so
// another window would expand every series again.
//
// So the forecast sees no further than that window (#347). `horizon` is handed
// to the fold and echoed back, so `runsOutOn: null` renders as "nothing booked
// through <date> outruns it". `PACKAGE_FORECAST_DAYS_FORWARD` must agree with
// `ATTENDANCE_DAYS_FORWARD` (src/domain/lessonAttendance.js), which
// test/ui/packageForecast.test.jsx asserts.
//
// ── `billable` ────────────────────────────────────────────────────────────
//
// A booked lesson's `billable` is the expression `useAttendance` builds: the
// lesson's billing (an event carrying `This event requires a make-up credit`
// makes `Unrecorded` not billable, #362) and a price above zero. `priceFor` is
// `useEvents`', so a forecast cannot disagree with the popover about a lesson;
// it answers `null` for a fixed-monthly student and for an hourly one on an
// all-day event, neither of which a package covers. Its `minutes` is the
// `durationMin` `useAttendance` writes onto the `consume`.
//
// `bookedLessonsOf` (#369) is shared with the studio-level list, and
// `convertibleLessonsOf` (#377) with the studio-level `+ Add Package`, so both
// convert the same lessons the student rail's does: a filter that admitted one
// too many would convert a lesson already charged. Its conditions follow MMS:
// marked (an unmarked lesson has no charge to remove), billable (`Absent,
// Notice Given` is skipped, #362), the student's own pricing
// (`shouldConsumePackage` asked of a sentinel balance), and priced above zero.
// The date bound is the wizard's package start date.
//
// The horizon is computed rather than read off the lessons: the last booked
// lesson says where the bookings stop, not where the looking stopped.
export {
  PACKAGE_FORECAST_DAYS_FORWARD, bookedLessonsOf, convertibleLessonsOf, forecastHorizon,
} from '../domain/packageSale';

export default function useStudentPackageForecast({
  student, history, priceFor, forecastFor,
}) {
  return useMemo(() => {
    if (!student) return null;
    const today = todayISO();
    const { lessons, booked } = bookedLessonsOf({ history, student, priceFor });
    // The booked half of the rejoin, alongside the fold's answer. The core
    // hands back ids and the caller owns the documents; the entry half is the
    // drawer's (`activityFor`), and this is the occurrences.
    return {
      ...forecastFor(student.id, booked, today, forecastHorizon(today)),
      lessonById: new Map(lessons.map((o) => [o.id, o])),
    };
  }, [student, history, priceFor, forecastFor]);
}
